import { ActionListContent } from 'modules/automation-rules/actions/components/action-list-content'
import { actionTitleLocoKeys } from 'modules/automation-rules/actions/constants/action-loco-keys'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { withErrorBoundary } from 'shared/hocs/with-error-boundary'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import {
  getWorkflowEditorUrl,
  getWorkflowEditorUrlWithStep,
} from 'shared/utils/get-workflow-editor-url'
import { ContactId } from '../../types/contact-interface'
import { useContactWorkflows } from '../hooks/use-contact-workflows'
import {
  ContactWorkflowDelay,
  ContactWorkflowDelayTypeEnum,
  ContactWorkflowInterface,
  WorkflowAction,
} from '../types/contact-workflow-interface'
import { CheckCircleIcon, TimeIcon, OpenInNewIcon } from '@systemeio/ui/icons'
import { Hint } from '@systemeio/ui'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import useUser from 'shared/hooks/use-user'
import { TIME_WITH_COUNT_LABELS } from 'shared/components/date-picker/time-delay-picker/constants'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'

interface ContactWorkflowsProps {
  contactId: ContactId
}

const ContactWorkflows = ({ contactId }: ContactWorkflowsProps) => {
  const { t, tICU } = useLocoTranslation()
  const { limit, pagination, setLimit, setPagination } = usePagination(true)
  const { user } = useUser()
  const { contactWorkflows } = useContactWorkflows({ contactId, limit, pagination })

  const CompletedStep = ({ taskCompletedAt }: { taskCompletedAt: string | undefined }) => (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-2">
        <CheckCircleIcon className="fill-green" />
        <span className="text-text-primary">
          {t('dashboard.contact_page.workflows.finished_workflow')}
        </span>
      </span>
      <span className="text-[10px]">
        {taskCompletedAt &&
          dateTimeFormat(taskCompletedAt, {
            dateFormat: user?.dateFormat,
            timeFormat: user?.timeFormat,
            timeZone: user?.timezone,
          })}
      </span>
    </div>
  )

  const NotCompletedStep = ({
    action,
    delay,
    taskCreatedAt,
  }: {
    action?: WorkflowAction
    delay?: ContactWorkflowDelay
    taskCreatedAt: string
  }) => (
    <div>
      {delay && <DelayInfo delay={delay} />}
      {action && <ActionInfo action={action} />}
      <span className="text-[10px]">
        {dateTimeFormat(taskCreatedAt, {
          dateFormat: user?.dateFormat,
          timeFormat: user?.timeFormat,
          timeZone: user?.timezone,
        })}
      </span>
    </div>
  )

  const DelayInfo = ({ delay }: { delay: ContactWorkflowDelay }) => (
    <div className="flex items-end gap-2">
      <TimeIcon className="fill-brand-primary" />
      <div className="flex gap-1">
        <span>{t('dashboard.contact_page.workflows.delay')}</span>
        <span>
          {delay.type === ContactWorkflowDelayTypeEnum.Date &&
            dateTimeFormat(delay.date, {
              dateFormat: user?.dateFormat,
              timeFormat: user?.timeFormat,
              timeZone: user?.timezone,
            })}
          {delay.type === ContactWorkflowDelayTypeEnum.Fixed &&
            tICU(TIME_WITH_COUNT_LABELS[delay.unit], {
              count: delay.duration,
            })}
        </span>
      </div>
    </div>
  )

  const ActionInfo = ({ action }: { action: WorkflowAction }) => (
    <div className="flex flex-col whitespace-normal">
      <span className="text-text-primary">{t(actionTitleLocoKeys[action.type])}</span>
      <span>
        <ActionListContent action={action} />
      </span>
    </div>
  )

  const columns: ColumnDefinitionInterface<ContactWorkflowInterface>[] = [
    {
      key: 'workflowStep',
      widthPercent: 50,
      header: t('dashboard.contact_page.workflows.headers.name'),
      onRender: ({ workflowStep: { workflow } }) => (
        <a
          href={getWorkflowEditorUrl(workflow.id)}
          rel="noreferrer"
          className="primary-link w-full whitespace-normal"
          target="__blank"
        >
          {workflow.name}
        </a>
      ),
    },
    {
      key: 'workflowStep',
      widthPercent: 50,
      header: t('dashboard.contact_page.workflows.headers.current_step'),
      onRender: ({
        workflowStep: { action, delay, workflow, id: workflowStepId },
        isCompleted,
        taskCreatedAt,
        taskCompletedAt,
      }) => (
        <LinkWithoutPrefetch
          href={getWorkflowEditorUrlWithStep(workflow.id, workflowStepId)}
          className="flex justify-between"
        >
          {isCompleted ? (
            <CompletedStep taskCompletedAt={taskCompletedAt} />
          ) : (
            <NotCompletedStep action={action} delay={delay} taskCreatedAt={taskCreatedAt} />
          )}
          <OpenInNewIcon className="fill-brand-primary" />
        </LinkWithoutPrefetch>
      ),
    },
  ]

  return (
    <ListingLayout
      counter={false}
      data={contactWorkflows}
      titleClassName={'truncate'}
      title={
        <Hint className={'truncate'} label={t('dashboard.contact_page.workflows.description')}>
          <h2 className={'h-[42px] truncate text-[17px] font-bold leading-[42px]'}>
            {t('header.top_menu.workflows')}
          </h2>
        </Hint>
      }
      type="compact"
    >
      <Table
        columns={columns}
        data={contactWorkflows}
        paginator={{ pagination, setPagination }}
        limiter={{ limit, setLimit }}
        nestedId="workflowStep"
      />
    </ListingLayout>
  )
}

export default withErrorBoundary(ContactWorkflows)
